/**
 * The ads' demo sites, shown live on the first screen of an ad visit (2026-10-04,
 * landing_pages/docs/preview-first-journey-handoff.md v2, step 1). From
 * landing_pages/saddle-ads/launch/2026-10/ads.csv: each ad links here with its demo's sentence as
 * `?idea=` and its name as `utm_content` (`Golf-W1-A`).
 *
 * These are PROTECTED slugs: shown, never modified, unlisted or republished (docs/PROTECTED-SLUGS.md).
 * How a demo is built (on the best model), checked and kept current: docs/DEMOS.md.
 * Only these may be framed by the builder (site.ts PREVIEW_DEMO_SLUGS keeps the same list, plus the
 * slugs they replaced).
 *
 * Wedding, Volunteer and Golf were rebuilt on 2026-10-12 from fuller prompts on Fable 5.1 ("blow
 * them away with the demo"): `madeFrom` is that prompt, and "Make one like this for you" builds from
 * it, so the visitor's build aims at the app they just looked at. `sentence` stays the ad's own, which
 * the ads still link with.
 */
export interface AdDemo {
  /** The ad family, as utm_content starts: `Golf` in `Golf-W1-A`. */
  ad: string;
  slug: string;
  /** The sentence the ad links with (`?idea=`). */
  sentence: string;
  /** The prompt the demo was built from, when it is not `sentence`. */
  madeFrom?: string;
  /** V6: the demo screen's button, in the visitor's own words ("Make my golf tracker"). */
  cta: string;
  /** V6: a hint shown over the demo for a few seconds once it has loaded. */
  tryIt: string;
  /**
   * Where the hint sits: near the top (the default), or just under the demo's top bar when what it
   * names is in that bar (the wedding's RSVP). Never over the thing it asks them to tap.
   */
  tryItAt?: 'top' | 'under-nav';
  /**
   * V6, variant b (analytics/experiment.ts): one question before the build. `prompt` is what is
   * built, with the answer in place of `{answer}`: the demo's own prompt with its specifics (Mia and
   * Theo, Lisbon, a food bank) swapped for theirs.
   */
  question: { ask: string; placeholder: string; prompt: string };
  /** V6: a still of the demo (web/public/demos/<slug>.jpg), shown until the live one has loaded. */
  poster: string;
}

/** What "Make one like this for you" builds: the prompt the demo itself came from. */
export const buildsFrom = (demo: AdDemo): string => demo.madeFrom ?? demo.sentence;

/** An answer is a name and a date, not an essay: anything past this is dropped. */
export const MAX_ANSWER = 120;

/** What variant b builds: the demo's prompt with their answer in it, or the demo's own when they gave none. */
export function answeredPrompt(demo: AdDemo, answer: string): string {
  const said = answer.replace(/\s+/g, ' ').trim().slice(0, MAX_ANSWER).trim();
  return said ? demo.question.prompt.replace('{answer}', said) : buildsFrom(demo);
}

/** The demo's prompt with `specifics` marked as where the answer goes. Throws if they are not in it. */
function swap(prompt: string, specifics: string): string {
  if (!prompt.includes(specifics)) throw new Error(`demos.ts: "${specifics}" is not in the prompt`);
  return prompt.replace(specifics, '{answer}');
}

const WEDDING_MADE_FROM = "A wedding website for Mia and Theo's weekend in Sonoma, June 11-13, 2027, that looks like an elegant editorial wedding magazine: a cover with their names, the date and a countdown, their story, the weekend's schedule day by day with times and places, where to stay with booking links, travel tips, and an RSVP where each guest says which events they are coming to, picks their meal, notes any dietary needs and can add a plus-one, then sees a warm confirmation.";
const TRIP_SENTENCE = "A shared itinerary for our group trip to Lisbon, May 14-19, 2027: each day planned with times and places, where we are staying, everyone's flights, and a packing list, so the whole group can see the plan on their phones.";
const POTLUCK_SENTENCE = "A potluck sign-up for our street's summer block party on Saturday, July 17, 2027: people say what dish they are bringing (main, side, dessert or drinks) so we do not end up with ten salads, and everyone can see what is already covered.";
const VOLUNTEER_MADE_FROM = "A volunteer scheduler for our food bank that looks polished and friendly. Volunteers see the next four weeks of shifts on a calendar, each with its time, its role and how many people it still needs, pick a shift and sign up with their name and email, and a shift closes when it is full. Each shift shows who has signed up so far, by first name, so the calendar feels busy and alive, and a short guide tells new volunteers what to bring and where to go.";
const GOLF_MADE_FROM = "A golf round tracker that looks and feels like a premium sports app. Before teeing off I take a photo of the course's scorecard and the app reads each hole's par and yardage, so the round is set up for me. During the round I log every stroke hole by hole with big thumb-friendly buttons: the club, roughly how far it went, and where it ended up (fairway, green, rough, bunker or water). After the round, and across all my rounds, it shows rich analytics: scoring average and its trend, score by par 3, 4 and 5, fairways hit, greens in regulation, putts per round, and for every club the average and longest distance, how often it is a good shot, and which club costs me the most strokes, all with clean charts.";
const CAFE_SENTENCE = "Staff schedule for my café. Every Sunday night I post next week's shifts: open 6:30 to 12, mid 11 to 4, close 2 to 7, two people each. Staff pick their shifts with their first name, and a full shift stops taking sign-ups.";

export const AD_DEMOS: AdDemo[] = [
  {
    ad: 'Wedding',
    slug: 'mia-theo-in-sonoma',
    sentence: "A wedding website for Mia and Theo's weekend in Sonoma, June 11-13, 2027: the schedule for each day, where to stay, and an RSVP where each guest says if they're coming, picks their meal and can add a plus-one.",
    madeFrom: WEDDING_MADE_FROM,
    cta: 'Make my wedding website',
    tryIt: 'Try it: open the RSVP',
    // RSVP is in the wedding site's top bar (and its cover's Reply button is near the bottom).
    tryItAt: 'under-nav',
    question: {
      ask: 'Whose wedding, and when?',
      placeholder: 'Sam and Alex, May 22 2027',
      prompt: swap(WEDDING_MADE_FROM, "Mia and Theo's weekend in Sonoma, June 11-13, 2027"),
    },
    poster: '/demos/mia-theo-in-sonoma.jpg',
  },
  {
    ad: 'Trip',
    slug: 'lisbon-2027-the-plan',
    sentence: TRIP_SENTENCE,
    cta: 'Make my trip plan',
    tryIt: 'Try it: open Flights',
    question: { ask: 'Where are you going, and when?', placeholder: 'Mexico City, March 3-8', prompt: swap(TRIP_SENTENCE, 'Lisbon, May 14-19, 2027') },
    poster: '/demos/lisbon-2027-the-plan.jpg',
  },
  {
    ad: 'Potluck',
    slug: 'block-party-potluck-2',
    sentence: POTLUCK_SENTENCE,
    cta: 'Make my potluck sign-up',
    tryIt: "Try it: see what's covered",
    question: {
      ask: "What's the occasion, and when?",
      placeholder: 'Our office holiday party, Friday, December 12',
      prompt: swap(POTLUCK_SENTENCE, "our street's summer block party on Saturday, July 17, 2027"),
    },
    poster: '/demos/block-party-potluck-2.jpg',
  },
  {
    ad: 'Volunteer',
    slug: 'pantry-shift-board',
    sentence: "A volunteer sign-up for our food bank: a calendar of the next four weeks of shifts, each with a time and how many volunteers it needs; volunteers pick a shift and sign up, and a shift closes when it's full.",
    madeFrom: VOLUNTEER_MADE_FROM,
    cta: 'Make my volunteer schedule',
    tryIt: 'Try it: tap a shift',
    question: { ask: "What's your organization called?", placeholder: 'Riverside Animal Shelter', prompt: swap(VOLUNTEER_MADE_FROM, 'our food bank') },
    poster: '/demos/pantry-shift-board.jpg',
  },
  {
    ad: 'Golf',
    slug: 'fairway-ledger-2',
    sentence: 'A golf tracker where I log each round with my score on every hole and the club I used for each stroke, and see over time how far and how well I hit each club, with charts.',
    madeFrom: GOLF_MADE_FROM,
    cta: 'Make my golf tracker',
    // Logging a stroke meets the demo's sign-in wall; Stats, in its bottom bar, is open to anyone.
    tryIt: 'Try it: tap Stats at the bottom',
    question: { ask: "What's your home course?", placeholder: 'Pebble Creek Golf Club', prompt: `${GOLF_MADE_FROM} My home course is {answer}.` },
    poster: '/demos/fairway-ledger-2.jpg',
  },
  {
    // A café rota for the Scheduling ads (schedule-demo-handoff, 2026-10-12), rebuilt on 2026-10-13 from
    // the same sentence as caf-shift-board: that board belongs to another account and drew 6:30 shifts
    // from 13:30 (docs/DEMOS.md).
    ad: 'Schedule',
    slug: 'caf-shift-picker',
    sentence: CAFE_SENTENCE,
    cta: 'Make my café schedule',
    tryIt: 'Try it: tap an open shift',
    question: { ask: "What's your café called?", placeholder: 'Bean There', prompt: swap(CAFE_SENTENCE, 'my café.').replace('{answer}', 'my café, {answer}.') },
    poster: '/demos/caf-shift-picker.jpg',
  },
];

export const SITE_ORIGIN = 'https://app.saddle-sites.com';

/** The demo an ad visit came for: by the ad's name first, then by the exact sentence it linked. */
export function demoForVisit(utmContent: string | undefined, idea: string | undefined): AdDemo | null {
  const family = (utmContent ?? '').split('-')[0]?.toLowerCase();
  const byAd = family ? AD_DEMOS.find((d) => d.ad.toLowerCase() === family) : undefined;
  if (byAd) return byAd;
  const said = (idea ?? '').trim();
  return said ? AD_DEMOS.find((d) => d.sentence === said) ?? null : null;
}

/**
 * The demo a build's idea came from: its ad's sentence, the prompt the demo was made from, or variant
 * b's prompt with an answer in it. Null for anybody's own words.
 */
export function demoForIdea(idea: string): AdDemo | null {
  const said = idea.trim();
  if (!said) return null;
  return AD_DEMOS.find((d) => {
    if (said === d.sentence || said === buildsFrom(d)) return true;
    const [before, after] = d.question.prompt.split('{answer}');
    return said.length > before.length + after.length && said.startsWith(before) && said.endsWith(after);
  }) ?? null;
}
